import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";

import { Modal } from "./Modal";

import { HeaderModal } from "./Header";
import { FooterModal } from "./Footer";
import { Body } from "./Body";
import allImgPaths from "assets/images";
import FileExplorer from "../Explorer";
import FileUploadProgress from "../FileUploadProgress";

import {
  createPatent,
  createPatentPresignedUrl,
  createUrl,
  patentUploadStatus,
  s3PutUrlWithProgress,
  deletePatentById,
  verifyPatentNumbers,
  processVerifiedPatents,
} from "services/project";
import Toast from "../Toast";
import { InputTextField } from "components/InputField";
import { PROJECT_CONSTANTS } from "utils/constant";
import PatentVerificationResults from "../PatentVerificationResults";
import PatentChipsInput from "../PatentChipsInput";
import { isPublicWalkthroughProject } from "utils/walkthroughLauncher";

interface Iprops {
  isShow: boolean;
  setIsShow: Dispatch<SetStateAction<boolean>>;
  setToast: any;
  projectId: string;
  setRefreshData: Dispatch<SetStateAction<boolean>>;
  type: string;
}

function AddComparisonData({
  isShow,
  setIsShow,
  setToast,
  projectId,
  setRefreshData,
  type,
}: Iprops) {
  // Read-only demo project: verify is allowed, but the final add actions
  // (Continue / Add) are disabled — no new targets can be added.
  const isReadOnlyDemo = isPublicWalkthroughProject(projectId);
  const [toggle, setToggle] = useState(2);
  const [option, setOption] = useState("external upload");
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const [filePreviews, setFilePreviews] = useState<any[]>([]);
  const [isAPICalled, setIsAPICalled] = useState(false);
  const [url, setUrl] = useState("");
  const [urlTouched, setUrlTouched] = useState(false);
  const [urlLabel, setUrlLabel] = useState("");
  const [urlLabelTouched, setUrlLabelTouched] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const abortControllersRef = useRef<AbortController[]>([]);
  const [patentNumber, setPatentNumber] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [hasFailedPatents, setHasFailedPatents] = useState(false);
  const [patentVerificationResults, setPatentVerificationResults] = useState<
    { number: string; verified?: boolean }[]
  >([]);

  const [fileProgress, setFileProgress] = useState<{ [key: string]: number }>(
    {}
  );
  const [uploadingFiles, setUploadingFiles] = useState<boolean>(false);

  // Patent processing state
  const [processingPatents, setProcessingPatents] = useState<boolean>(false);
  const [processingComplete, setProcessingComplete] = useState<boolean>(false);

  useEffect(() => {
    if (type === "file" || type === "patent") {
      setToggle(2);
    } else if (type === "url") {
      setToggle(3);
    } else if (type === "patent_number") {
      setToggle(0);
    }
  }, []);

  const isUrlValid = useMemo(() => {
    try {
      new URL(url.trim());
      return url.trim().length > 0;
    } catch {
      return false;
    }
  }, [url]);

  const handleUrlChange = (e: any) => {
    setUrl(e.target.value.trim());
    setUrlTouched(true);
  };

  const handleUrlLabelChange = (e: any) => {
    setUrlLabel(e.target.value);
    setUrlLabelTouched(true);
  };

  const onCloseModal = () => {
    setIsShow(false);
    setSelectedFiles([]);
    setFilePreviews([]);
    setIsAPICalled(false);
    setUrl("");
    setUrlTouched(false);
    setUrlLabel("");
    setUrlLabelTouched(false);
    setUploadComplete(false);
    setPatentNumber("");
    setIsVerifying(false);
    setIsVerified(false);
    setPatentVerificationResults([]);
  };

  // Helper function to clean up any created resources
  const cleanupResources = async (resources: any[]) => {
    for (const resource of resources) {
      try {
        if (resource.type === "patent" && resource.id) {
          await deletePatentById(resource.id);
          console.log(
            `Deleted patent: ${resource.id} for file ${resource.file}`
          );
        }
      } catch (cleanupError) {
        console.error(`Failed to clean up resource: ${cleanupError}`);
      }
    }
  };

  // Function to cancel ongoing uploads
  const cancelUploads = async () => {
    console.log("Canceling uploads...");

    // Abort all active controllers
    abortControllersRef.current.forEach((controller) => {
      try {
        controller.abort();
      } catch (error) {
        console.error("Error canceling upload:", error);
      }
    });

    // Reset states
    setUploadingFiles(false);

    // Clear any partial file progress
    setFileProgress({});

    // Close the modal immediately
    onCloseModal();
  };

  // Custom implementation of upload with progress that properly handles abort signals
  const uploadWithProgress = async (
    payload: any,
    progressCallback: (progress: number) => void,
    signal: AbortSignal
  ) => {
    const { url, file, type } = payload;

    try {
      // Check if already aborted
      if (signal && signal.aborted) {
        throw new Error("Upload canceled");
      }

      const xhr = new XMLHttpRequest();

      // Set up promise to handle completion
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.open("PUT", url);
        xhr.setRequestHeader("Content-Type", type);

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({ status: xhr.status });
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        };

        xhr.onerror = () => {
          reject(new Error("Network error during upload"));
        };

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = (event.loaded / event.total) * 100;
            progressCallback(percentComplete);
          }
        };

        // Handle abort
        if (signal) {
          signal.addEventListener("abort", () => {
            xhr.abort();
            reject(new Error("Upload canceled"));
          });
        }
      });

      // Start the upload
      xhr.send(file);
      return await uploadPromise;
    } catch (error: any) {
      if (error.message === "Upload canceled") {
        console.log("Upload was canceled");
      }
      throw error;
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let recievedFiles: any[] = (e.target.files || []) as any[];
    const files = Array.from(recievedFiles);
    handleFiles(files);
  };

  const handleFiles = async (files: any[]) => {
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ];

    let validFiles = files.filter((file) => allowedTypes.includes(file.type));

    if (validFiles.length !== files.length) {
      Toast.error("Error", "Only PDF and DOCX files are allowed");
    }

    if (validFiles.length === 0) {
      return; // Don't proceed if no valid files
    }

    // Check for duplicate files that already exist in the selected files array
    const fileNames = selectedFiles.map((f) => f.name);
    const uniqueValidFiles = validFiles.filter(
      (file) => !fileNames.includes(file.name)
    );

    if (uniqueValidFiles.length === 0) {
      Toast.info("Info", "These files are already in the upload list");
      return; // Don't proceed if no new files
    }

    const previewPromises: Promise<string>[] = [];
    uniqueValidFiles.forEach((file) => {
      const promise = new Promise<string>((resolve) => {
        displayPreview(file, (preview) => {
          resolve(preview);
        });
      });
      previewPromises.push(promise);
    });

    // Set selected files first - adding only unique files to the existing list
    setSelectedFiles((prevFiles) => {
      const newFiles = [...prevFiles, ...uniqueValidFiles];

      // Then start upload after files are set
      Promise.all(previewPromises).then((previews) => {
        setFilePreviews((prevPreviews) => [...prevPreviews, ...previews]);

        // Automatically start upload if we have valid files for file/patent types
        if (
          uniqueValidFiles.length > 0 &&
          (type === "file" || type === "patent")
        ) {
          // We need to wait for state to update before triggering upload
          setTimeout(() => {
            startFileUpload(uniqueValidFiles); // Only upload the new unique files
          }, 0);
        }
      });

      return newFiles;
    });
  };

  // New function to start the upload process
  const startFileUpload = async (filesToUpload: any[]) => {
    if (!filesToUpload.length) return;

    const userData: any = JSON.parse(
      localStorage.getItem("userData") as string
    );
    let selectedTeam: any = JSON.parse(
      localStorage.getItem("selectedTeam") as string
    );

    // Keep track of created resources for cleanup if canceled
    const createdResources: any[] = [];

    // Clear previous abort controllers and reset state
    abortControllersRef.current = [];
    setUploadComplete(false);
    setIsAPICalled(true);
    setUploadingFiles(true);

    try {
      const uploadPromises = filesToUpload.map(async (currentFile) => {
        // Create new abort controller for this upload
        const controller = new AbortController();
        abortControllersRef.current.push(controller);
        const signal = controller.signal;

        try {
          // Check if already canceled
          if (signal.aborted) {
            throw new Error("Upload canceled");
          }

          // Step 1: Create patent
          // let payload = { patent_id: currentFile.name.split(".pdf")[0] };
          const nameWithoutExtension = currentFile.name.toLowerCase().replace(/\.(pdf|docx?)$/, "");
          const payload = { patent_id: nameWithoutExtension };
          const { data, status } = await createPatent(payload);

          if (!data) {
            throw new Error(
              `Failed to create patent for file ${currentFile.name}`
            );
          }

          // Track created resource for potential cleanup
          createdResources.push({
            type: "patent",
            id: data._id,
            file: currentFile.name,
          });

          // Check if canceled after patent creation
          if (signal.aborted) {
            throw new Error("Upload canceled");
          }

          // Step 2: Get presigned URL
          let payload2: any = {
            team_id: selectedTeam.currentTeamId,
            project_id: projectId,
            patent_id: data._id,
            file_name: currentFile.name,
            content_type: currentFile.type,
            patent_category: PROJECT_CONSTANTS.COMPARE,
          };

          if (type === "file") {
            payload2.patent_type = PROJECT_CONSTANTS.EXTERNAL;
          } else if (type === "patent") {
            payload2.patent_type = PROJECT_CONSTANTS.PATENT;
            payload2.source = "Manual Upload";
          }

          const step2Response: any = await createPatentPresignedUrl(payload2);
          if (!step2Response?.url) {
            throw new Error(
              `Failed to get presigned URL for file ${currentFile.name}`
            );
          }

          // Track file_id
          createdResources[createdResources.length - 1].file_id =
            step2Response.file_id;

          // Check if canceled after getting URL
          if (signal.aborted) {
            throw new Error("Upload canceled");
          }

          // Step 3: Upload to S3
          const step3payload: any = {
            url: step2Response.url,
            file: currentFile,
            type: currentFile.type,
          };

          // Use our custom upload function that properly handles abort
          const response: any = await uploadWithProgress(
            step3payload,
            (progress) => {
              if (signal.aborted) {
                throw new Error("Upload canceled");
              }
              setFileProgress((prev) => ({
                ...prev,
                [currentFile.name]: Math.round(progress),
              }));
            },
            signal
          );

          // Check if canceled after upload
          if (signal.aborted) {
            throw new Error("Upload canceled");
          }

          // Step 4: Update upload status
          const fileStatus = response.status === 200 ? "uploaded" : "pending";
          await patentUploadStatus({
            file_id: step2Response.file_id,
            file_status: fileStatus,
          });

          return { success: true, fileName: currentFile.name };
        } catch (error: any) {
          if (error.message === "Upload canceled") {
            console.log(`Upload for ${currentFile.name} was canceled`);
            return {
              success: false,
              fileName: currentFile.name,
              canceled: true,
            };
          }
          throw error;
        }
      });

      // Use Promise.allSettled instead of Promise.all to continue even if some promises reject
      const results = await Promise.allSettled(uploadPromises);

      // Check if any uploads were canceled or rejected
      const isCanceled = abortControllersRef.current.some(
        (controller) => controller.signal.aborted
      );

      if (isCanceled) {
        console.log("Upload was canceled by user, cleaning up resources...");

        // Clean up any created resources
        await cleanupResources(createdResources);

        // Reset UI state
        setUploadingFiles(false);
        setFileProgress({});
        return;
      }

      // If we get here, it means no cancellation occurred
      const successfulUploads = results
        .filter((r) => r.status === "fulfilled")
        .map((r) => (r as PromiseFulfilledResult<any>).value)
        .filter((r) => r.success);

      const allSuccessful = successfulUploads.length === filesToUpload.length;

      if (allSuccessful) {
        let successMessage: any;
        if (type === "file") {
          successMessage =
            successfulUploads.length > 1
              ? `All files added successfully`
              : `File added successfully`;
        } else if (type === "patent") {
          successMessage =
            successfulUploads.length > 1
              ? `All patents added successfully`
              : `Patent added successfully`;
        }
        Toast.success("Success", successMessage);
        setRefreshData(true);

        // Mark upload as complete but don't close modal
        setUploadComplete(true);
        setUploadingFiles(false);
      } else if (successfulUploads.length > 0) {
        // Some files uploaded successfully
        Toast.success(
          "Success",
          `${successfulUploads.length} file(s) uploaded successfully`
        );
        setRefreshData(true);
        setUploadComplete(true);
        setUploadingFiles(false);
      } else {
        // No files uploaded successfully and it wasn't a cancellation
        setUploadingFiles(false);
      }
    } catch (error: any) {
      console.error("Upload error:", error);

      // Reset state
      setUploadingFiles(false);

      // Only show error if not canceled by user and not already handled
      if (error.message !== "Upload canceled") {
        Toast.error("Error", "Failed to upload files");
      }
    }
  };

  const resetModal = () => {
    setIsShow(false);
    setSelectedFiles([]);
    setFilePreviews([]);
    setIsAPICalled(false);
    setFileProgress({});
    setUploadingFiles(false);
    setUploadComplete(false);
    setUrl("");
    setUrlLabel("");
    setUrlLabelTouched(false);
    // Clear abort controllers
    abortControllersRef.current = [];
  };

  const displayPreview = (file: File, callback: (preview: string) => void) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      if (reader.result) {
        callback(reader.result as string);
      } else {
        callback("");
      }
    };
  };

  const verifyPatents = async () => {
    if (!patentNumber.trim()) return;

    setIsVerifying(true);
    setIsVerified(false);
    setHasFailedPatents(false);
    try {
      // Split the patent numbers by comma and trim whitespace
      const patentNumbers = patentNumber
        .split(",")
        .map((num) => num.trim())
        .filter((num) => num.length > 0);

      // Check for duplicate patent numbers
      const uniquePatents = new Set(patentNumbers);
      if (uniquePatents.size !== patentNumbers.length) {
        // Find duplicates
        const duplicates = patentNumbers.filter(
          (num, index) => patentNumbers.indexOf(num) !== index
        );

        Toast.error(
          "Error",
          `Duplicate patent numbers found: ${duplicates.join(", ")}`
        );
        setIsVerifying(false);
        return;
      }

      // Create initial patent verification results with pending status
      const initialResults = patentNumbers.map((number) => ({
        number,
        verified: undefined,
      }));
      setPatentVerificationResults(initialResults);

      // Construct the payload for verification
      const payload = {
        patent_numbers: patentNumbers,
        project_id: projectId,
        team_id: localStorage.getItem("selectedTeam")
          ? JSON.parse(localStorage.getItem("selectedTeam") as string)
            .currentTeamId
          : undefined,
      };

      // Call the backend API for verification
      const verificationResponse = await verifyPatentNumbers(payload);
      console.log("Patent verification response:", verificationResponse);

      if (verificationResponse?.data) {
        const { data } = verificationResponse;

        // Extract verification results and patent lists
        const verificationResults = data.verification_results || {};
        const verifiedPatents = data.verified_patents || [];
        const failedPatents = data.failed_patents || [];

        // Update verification results based on API response
        const updatedResults = initialResults.map((patent) => ({
          ...patent,
          verified: verificationResults[patent.number] === true,
        }));

        setPatentVerificationResults(updatedResults);

        // Check if any patents are verified
        const hasVerifiedPatents = verifiedPatents.length > 0;
        const hasAnyFailedPatents = failedPatents.length > 0;
        setIsVerified(hasVerifiedPatents);
        setHasFailedPatents(hasAnyFailedPatents);

        if (hasVerifiedPatents) {
          Toast.success(
            "Success",
            `${verifiedPatents.length} patent(s) verified successfully`
          );
        } else {
          Toast.warning("Warning", "No valid patents found");
        }
      } else {
        Toast.error(
          "Error",
          "Failed to verify patents. Invalid response format."
        );
      }
    } catch (error: any) {
      console.error("Patent verification failed:", error);

      // Mark all patents as verification failed
      setPatentVerificationResults((prev) =>
        prev.map((patent) => ({
          ...patent,
          verified: false,
        }))
      );

      if (error?.message) {
        const errorMessage = Array.isArray(error.message)
          ? error.message[0]
          : error.message;
        Toast.error("Error", errorMessage);
      } else {
        Toast.error("Error", "Failed to verify patents. Please try again.");
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const processPatents = async () => {
    try {
      setProcessingPatents(true);
      setProcessingComplete(false);

      // Get verified patents
      const verifiedPatents = patentVerificationResults
        .filter((patent) => patent.verified)
        .map((patent) => patent.number);

      if (verifiedPatents.length === 0) {
        Toast.warning("Warning", "No verified patents to process");
        setProcessingPatents(false);
        return;
      }

      // Prepare payload for processing
      const payload = {
        patent_numbers: verifiedPatents,
        project_id: projectId,
        team_id: localStorage.getItem("selectedTeam")
          ? JSON.parse(localStorage.getItem("selectedTeam") as string)
            .currentTeamId
          : undefined,
        patent_category: PROJECT_CONSTANTS.COMPARE,
        patent_type: PROJECT_CONSTANTS.PATENT,
        patent_source: "With Patent Number",
      };

      // Call API to process the patents
      const response = await processVerifiedPatents(payload);
      console.log("Process patents response:", response);

      // Set processing complete
      setProcessingComplete(true);
      setProcessingPatents(false);

      // Show success message (customized)
      Toast.success(
        "Success",
        verifiedPatents.length > 1
          ? "Patents added successfully"
          : "Patent added successfully"
      );

      // Refresh the data to show new patents
      setRefreshData((prev) => !prev);
      onCloseModal();
    } catch (error: any) {
      console.error("Error processing patents:", error);
      setProcessingPatents(false);
      setProcessingComplete(false);

      if (error?.message) {
        const errorMessage = Array.isArray(error.message)
          ? error.message[0]
          : error.message;
        Toast.error("Error", errorMessage);
      } else {
        Toast.error("Error", "Failed to process patents. Please try again.");
      }
    }
  };

  const handleContinueClick = () => {
    if (toggle === 0) {
      // Patent number
      if (!isVerified) return;

      // If patents are verified, process them
      processPatents();
    } else if (toggle === 2) {
      // File upload
    }
  };

  const submit = async (e: any) => {
    e?.preventDefault();

    if (toggle === 0) {
      // Patent number
      if (isVerified && !processingPatents && !processingComplete) {
        // If patents are verified and not processed, process them
        processPatents();
        return;
      }

      if (processingPatents) {
        // Don't close modal if still processing
        return;
      }

      // If processing is complete, close the modal
      if (processingComplete) {
        onCloseModal();
        return;
      }
    } else if (toggle === 3 && type === "url") {
      setUploadingFiles(true);
      try {
        if (!isUrlValid || urlLabel.trim().length === 0) {
          setUploadingFiles(false);
          return;
        }

        const userData: any = JSON.parse(
          localStorage.getItem("userData") as string
        );
        let selectedTeam: any = JSON.parse(
          localStorage.getItem("selectedTeam") as string
        );

        let payload: any = {
          url,
          url_label: urlLabel.trim(),
          project_id: projectId,
          team_id: selectedTeam.currentTeamId,
        };

        await createUrl(payload);
        setToggle(2);
        setIsShow(false);
        setRefreshData((prev) => !prev);
        Toast.success("Success", "URL added successfully");
      } catch (error: any) {
        console.error("Error while creating url", error);
        setUploadingFiles(false);
        if (error?.message) {
          const errorMessage = Array.isArray(error.message)
            ? error.message[0]
            : error.message;
          Toast.error("Error", errorMessage);
        } else {
          Toast.error(
            "Error",
            "Error while creating url please try again later"
          );
        }
      }
    } else if (
      toggle === 2 &&
      selectedFiles.length > 0 &&
      (type === "file" || type === "patent")
    ) {
      // For file/patent, we now use startFileUpload which is called directly after file selection
      startFileUpload(selectedFiles);
    } else if (toggle === 0 && type === "patent_number") {
      // For patent numbers that have been verified
      if (!isVerified) {
        Toast.error("Error", "Please verify the patent numbers first");
        return;
      }

      // Patents were created during the verification step,
      // so now we just need to close the modal and refresh
      try {
        Toast.success("Success", "Patents added successfully");
        setRefreshData((prev) => !prev);
        onCloseModal();
      } catch (error: any) {
        console.error("Error adding patents:", error);
        if (error?.message) {
          const errorMessage = Array.isArray(error.message)
            ? error.message[0]
            : error.message;
          Toast.error("Error", errorMessage);
        } else {
          Toast.error("Error", "Failed to add patents. Please try again.");
        }
      }
    }
  };

  return (
    <>
      <Modal className={"modal-wrapper max-w-[700px]"} isShow={isShow}>
        <form onSubmit={submit}>
          {/* Modal bg Shade */}

          <HeaderModal
            title={"test"}
            isShowHeader={true}
            className="modal-header pt-6 px-6"
          >
            <div className="flex items-center justify-between pb-3">
              <img
                className="cursor-pointer"
                src={allImgPaths.addPatentLogo}
                alt="remove"
              />
              {/* Close Icon */}
              <img
                className="modal-close modal-toggle cursor-pointer hover:bg-[#f2f4f7] rounded w-5 p-1 mb-4"
                id="modal-toggle"
                src={allImgPaths.closeIcon}
                onClick={onCloseModal}
                alt="close icon"
              />
            </div>

            <h2 className="text-base text-[#0A090B] font-interSemiBold pb-1">
              Add Comparison Data
            </h2>
            <p className="text-sm text-[#4F4D55]">
              {type === "file" && "Add Files to compare with the source patent"}
              {type === "patent" &&
                "Add Patents to compare with the source patent"}
              {type === "url" && "Add URLs to be crawled and compared"}
              {type === "patent_number" &&
                "Add patent number(s) to compare with"}
            </p>
          </HeaderModal>

          <Body title="" isShowBody={true} className="modal-body px-6 pt-3">
            <div className="modal-container mt-3">
              {toggle == 0 && (
                <div className="">
                  <div className="text-[#2D2B32] font-interMedium text-left  pb-1.5 flex gap-1">
                    Enter Patent #
                    <img src={allImgPaths.questionCircle} alt="help" />
                  </div>
                  <div className="mb-2">
                    <PatentChipsInput
                      value={patentNumber}
                      onChange={(value) => {
                        setPatentNumber(value);
                        // Reset verification state when input changes
                        if (isVerified) {
                          setIsVerified(false);
                          setPatentVerificationResults([]);
                          setHasFailedPatents(false);
                        }
                      }}
                      placeholder="Enter patent numbers separated by commas"
                    />
                  </div>
                  <div className="flex justify-end mt-4">
                    <button
                      type="button"
                      onClick={verifyPatents}
                      disabled={
                        !patentNumber.trim() || isVerifying || isVerified
                      }
                      className={`text-white font-interSemiBold rounded-lg px-3.5 py-2 !text-base ${!patentNumber.trim() || isVerifying || isVerified
                        ? "button-disabled"
                        : "bg-[#1751D0] hover:bg-[#113B98]"
                        }`}
                    >
                      {isVerifying ? "Verifying..." : "Verify"}
                    </button>
                  </div>

                  <PatentVerificationResults
                    results={patentVerificationResults}
                    processingPatents={processingPatents}
                    processingComplete={processingComplete}
                  />
                </div>
              )}

              {toggle == 1 && (
                <div className="">
                  <div className="w-full">
                    <FileExplorer />
                  </div>
                </div>
              )}

              {toggle == 2 && (
                <div className="bg-[#F9FAFB] px-3 pl-4 py-3 rounded-md">
                  <div
                    className="relative border border-[#C9C9CC] hover:border-gray-400 border-dashed rounded-lg p-2 w-[99%]"
                    id="dropzone"
                    onDragOver={(e) => e.preventDefault()}
                    onDragEnter={(e) => e.preventDefault()}
                    onDragLeave={() => { }}
                    onDrop={handleDrop}
                  >
                    <input
                      id="photo-dropbox"
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx"
                      className="absolute inset-0 w-full h-full opacity-0 z-50"
                      onChange={handleFileChange}
                    />
                    <label
                      className="flex cursor-pointer gap-1 w-full px-3 py-2 bg-[#F9FAFB] rounded-lg"
                      htmlFor="photo-dropbox"
                    >
                      <input
                        id="photo-dropbox"
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx"
                        className="sr-only"
                        onChange={(e) => console.log(e.target.files)}
                      />
                      <img src={allImgPaths.uploadIcon} alt="upload.svg" />
                      <span className="px-2">
                        Drag & drop patent file or{" "}
                        <span className="text-[#1751D0]">browse files</span>
                      </span>
                    </label>
                  </div>

                  {selectedFiles.length > 0 && (
                    <FileUploadProgress
                      files={selectedFiles}
                      progress={fileProgress}
                    />
                  )}
                </div>
              )}
              {toggle == 3 && (
                <div className="">
                  <div className="text-[#2D2B32] font-interMedium text-left pb-1.5 flex gap-1">
                    URL Name
                    <img src={allImgPaths.questionCircle} alt="help" />
                  </div>
                  <div className="mb-4">
                    <InputTextField
                      className="placeholder:text-[#7F7D83] placeholder:text-[15px] text-base text-[#344054] w-full"
                      name={"urlLabel"}
                      placeholder={"Enter URL Name"}
                      onChange={handleUrlLabelChange}
                      value={urlLabel}
                      err={
                        urlLabelTouched && urlLabel.trim().length === 0
                          ? "URL Name is required"
                          : undefined
                      }
                    />
                  </div>
                  <div className="text-[#2D2B32] font-interMedium text-left  pb-1.5 flex gap-1">
                    URL
                    <img src={allImgPaths.questionCircle} alt="help" />
                  </div>
                  <div className="mb-4">
                    <InputTextField
                      className="placeholder:text-[#7F7D83] placeholder:text-[15px] text-base text-[#344054] w-full"
                      name={"url"}
                      placeholder={"Enter Url"}
                      onChange={handleUrlChange}
                      value={url}
                      err={
                        urlTouched && !isUrlValid
                          ? "Please enter a valid URL (e.g., https://example.com)"
                          : undefined
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          </Body>
          <FooterModal
            title=""
            isShowFooter={true}
            className="modal-footer pt-8 px-6 pb-4"
          >
            <div className="flex items-center gap-x-3 justify-center">
              {(type === "file" || type === "patent") && (
                <>
                  <button
                    onClick={cancelUploads}
                    className="text-[#344054] font-interSemiBold border border-[#D0D5DD] rounded-lg tracking-wide py-2.5 px-2.5 !text-base flex-1 hover:bg-[#f2f4f7]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={resetModal}
                    disabled={isReadOnlyDemo || !uploadComplete || uploadingFiles}
                    className={`!text-base text-white bg-[#1751D0] hover:bg-[#113B98] font-interSemiBold rounded-lg px-3.5 py-2.5 flex-1 ${isReadOnlyDemo || !uploadComplete || uploadingFiles ? "button-disabled" : ""
                      }`}
                  >
                    Continue
                  </button>
                </>
              )}
              {type === "url" && (
                <>
                  <button
                    onClick={onCloseModal}
                    className="text-[#344054] font-interSemiBold border border-[#D0D5DD] rounded-lg tracking-wide py-2.5 px-2.5 !text-base flex-1 hover:bg-[#f2f4f7]"
                  >
                    Cancel
                  </button>
                  <button
                    className={`!text-base text-white bg-[#1751D0] hover:bg-[#113B98] font-interSemiBold rounded-lg px-3.5 py-2.5 flex-1 ${isReadOnlyDemo ||
                      !isUrlValid ||
                      uploadingFiles ||
                      urlLabel.trim().length === 0
                      ? "button-disabled"
                      : ""
                      }`}
                    type="submit"
                    disabled={
                      isReadOnlyDemo ||
                      !isUrlValid ||
                      uploadingFiles ||
                      urlLabel.trim().length === 0
                    }
                  >
                    Add
                  </button>
                </>
              )}

              {type === "patent_number" && (
                <>
                  <button
                    onClick={onCloseModal}
                    className="text-[#344054] font-interSemiBold border border-[#D0D5DD] rounded-lg tracking-wide py-2.5 px-2.5 !text-base flex-1 hover:bg-[#f2f4f7]"
                  >
                    Cancel
                  </button>
                  <button
                    disabled={
                      isReadOnlyDemo || !isVerified || processingPatents || hasFailedPatents
                    }
                    className={`!text-base text-white font-interSemiBold rounded-lg px-3.5 py-2.5 flex-1 ${isReadOnlyDemo || !isVerified || processingPatents || hasFailedPatents
                      ? "button-disabled"
                      : "bg-[#1751D0] hover:bg-[#113B98]"
                      }`}
                    type="submit"
                    onClick={(e) => submit(e)}
                  >
                    Continue
                  </button>
                </>
              )}
            </div>
          </FooterModal>
        </form>
      </Modal>
    </>
  );
}
export default AddComparisonData;
